import React, { useEffect } from 'react';
import axios from 'axios';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { auth0Config, isAuth0Configured } from './Auth0Config';
import {
    normalizeAuth0User,
    setAuthActions,
    setAuthSnapshot
} from '../../Services/AuthService';

const jwtDecodeModule = require('jwt-decode');
const jwtDecode = jwtDecodeModule.default || jwtDecodeModule;

const isApplicationApiRequest = (url) => {
    if (!url || typeof url !== 'string') {
        return false;
    }

    return url.includes('/api/');
};

const AuthStateBridge = ({ children }) => {
    const {
        error,
        isAuthenticated,
        isLoading,
        user,
        getAccessTokenSilently,
        loginWithRedirect,
        logout
    } = useAuth0();

    useEffect(() => {
        setAuthActions({
            loginWithRedirect,
            logout,
            getAccessTokenSilently,
            isConfigReady: true
        });
    }, [loginWithRedirect, logout, getAccessTokenSilently]);

    useEffect(() => {
        const interceptorId = axios.interceptors.request.use(async (config) => {
            if (!isAuthenticated || !isApplicationApiRequest(config?.url)) {
                return config;
            }

            const headers = { ...(config.headers || {}) };
            try {
                const accessToken = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: auth0Config.audience || undefined,
                        scope: auth0Config.scope || undefined
                    }
                });

                if (accessToken) {
                    headers.Authorization = `Bearer ${accessToken}`;
                    delete headers.authorization;
                }
            } catch {
                const existingHeader = headers.Authorization || headers.authorization;
                if (typeof existingHeader === 'string' && existingHeader.trim().toLowerCase() === 'bearer null') {
                    delete headers.Authorization;
                    delete headers.authorization;
                }
            }

            return {
                ...config,
                headers
            };
        });

        return () => {
            axios.interceptors.request.eject(interceptorId);
        };
    }, [getAccessTokenSilently, isAuthenticated]);

    useEffect(() => {
        let cancelled = false;

        const syncState = async () => {
            if (isLoading) {
                setAuthSnapshot({ isLoading: true, auth0Available: true, error: null });
                return;
            }

            if (!isAuthenticated) {
                setAuthSnapshot({
                    isLoading: false,
                    isAuthenticated: false,
                    accessToken: null,
                    user: null,
                    auth0Available: true,
                    error: error || null
                });
                return;
            }

            const normalizedUser = normalizeAuth0User(user, null, user);

            setAuthSnapshot({
                isLoading: false,
                isAuthenticated: true,
                accessToken: null,
                user: normalizedUser,
                auth0Available: true,
                error: null
            });

            try {
                const accessToken = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: auth0Config.audience || undefined,
                        scope: auth0Config.scope || undefined
                    }
                });
                const decodedToken = jwtDecode(accessToken);

                if (cancelled) {
                    return;
                }

                setAuthSnapshot({
                    isAuthenticated: true,
                    accessToken,
                    user: normalizeAuth0User(decodedToken, accessToken, user),
                    error: null
                });
            } catch (tokenError) {
                if (cancelled) {
                    return;
                }

                setAuthSnapshot({
                    isAuthenticated: true,
                    accessToken: null,
                    user: normalizedUser,
                    auth0Available: true,
                    error: tokenError
                });
            }
        };

        syncState();

        return () => {
            cancelled = true;
        };
    }, [error, getAccessTokenSilently, isAuthenticated, isLoading, user]);

    return children;
};

const UnconfiguredAuth0Bridge = ({ children }) => {
    useEffect(() => {
        setAuthSnapshot({
            isLoading: false,
            isAuthenticated: false,
            accessToken: null,
            user: null,
            auth0Available: false,
            error: new Error('Auth0 is not configured')
        });
        setAuthActions({ isConfigReady: false });
    }, []);

    return children;
};

const Auth0ProviderWrapper = ({ children }) => {
    if (!isAuth0Configured()) {
        return <UnconfiguredAuth0Bridge>{children}</UnconfiguredAuth0Bridge>;
    }

    return (
        <Auth0Provider
            domain={auth0Config.domain}
            clientId={auth0Config.clientId}
            authorizationParams={{
                redirect_uri: auth0Config.redirectUri,
                audience: auth0Config.audience || undefined,
                scope: auth0Config.scope || undefined
            }}
        >
            <AuthStateBridge>{children}</AuthStateBridge>
        </Auth0Provider>
    );
};

export default Auth0ProviderWrapper;